import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select } from '@chapche/ui';
import {
    CookingStepCondition,
    CookingStepConditionType,
} from '../../services/recipeTypes';
import { Locale } from '../../services/commonTypes';
import {
    INGREDIENT_GROUP_OPTIONS,
    KASHA_CONSISTENCY_OPTIONS,
    PRODUCT_CONDITION_FIELDS,
    PRODUCT_FIELD_OTHER,
    formatConditionPreview,
    getProductFieldDef,
    isKnownProductField,
    parseGroupChips,
    serializeGroupChips,
    validateCondition,
} from '../../services/cookingStepConditionUtils';
import ProductCodeAutocomplete from './ProductCodeAutocomplete';

type ConditionRowProps = {
    condition: CookingStepCondition;
    onChange: (patch: Partial<CookingStepCondition>) => void;
    onRemove: () => void;
};

const ConditionRow: React.FC<ConditionRowProps> = ({
    condition,
    onChange,
    onRemove,
}) => {
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n);
    const errors = validateCondition(condition);
    const preview = formatConditionPreview(
        condition,
        t,
        i18n.resolvedLanguage || Locale.LOCALE_RU,
    );

    const conditionTypeOptions = [
        {
            value: CookingStepConditionType.PRODUCT_FIELD,
            label: t('app.page.cookingStep.conditionType.product_field'),
        },
        {
            value: CookingStepConditionType.PRODUCT_CODE,
            label: t('app.page.cookingStep.conditionType.product_code'),
        },
        {
            value: CookingStepConditionType.ANY_INGREDIENT_GROUP_PRESENT,
            label: t('app.page.cookingStep.conditionType.any_ingredient_group_present'),
        },
        {
            value: CookingStepConditionType.KASHA_CONSISTENCY,
            label: t('app.page.cookingStep.conditionType.kasha_consistency'),
        },
        {
            value: CookingStepConditionType.KASHA_CONSISTENCY_IN,
            label: t('app.page.cookingStep.conditionType.kasha_consistency_in'),
        },
    ];

    const ingredientGroupOptions = INGREDIENT_GROUP_OPTIONS.map((group) => ({
        value: group,
        label: t(`app.page.cookingStep.ingredientGroup.${group}`),
    }));

    const fieldOptions = useMemo(() => ([
        ...PRODUCT_CONDITION_FIELDS.map((field) => ({
            value: field.fieldName,
            label: t(field.labelKey),
        })),
        {
            value: PRODUCT_FIELD_OTHER,
            label: t('app.page.cookingStep.productField.other'),
        },
    ]), [t]);

    const selectedFieldValue = isKnownProductField(condition.fieldName)
        ? condition.fieldName
        : (condition.fieldName ? PRODUCT_FIELD_OTHER : '');

    const fieldDef = getProductFieldDef(condition.fieldName);
    const showBoolSelect = selectedFieldValue !== PRODUCT_FIELD_OTHER
        && fieldDef?.valueType === 'boolean';

    const boolOptions = [
        { value: 'true', label: t('app.page.cookingStep.bool.yes') },
        { value: 'false', label: t('app.page.cookingStep.bool.no') },
    ];

    const selectedGroups = parseGroupChips(condition.value);

    const toggleGroup = (group: string) => {
        const next = selectedGroups.includes(group)
            ? selectedGroups.filter((item) => item !== group)
            : [...selectedGroups, group];
        onChange({ value: serializeGroupChips(next) });
    };

    const toggleConsistency = (code: string) => {
        if (condition.conditionType === CookingStepConditionType.KASHA_CONSISTENCY
            || condition.conditionType === 'kasha_consistency') {
            onChange({ value: code });
            return;
        }
        const next = selectedGroups.includes(code)
            ? selectedGroups.filter((item) => item !== code)
            : [...selectedGroups, code];
        onChange({ value: serializeGroupChips(next) });
    };

    const isProductField = condition.conditionType === CookingStepConditionType.PRODUCT_FIELD
        || condition.conditionType === 'product_field';
    const isProductCode = condition.conditionType === CookingStepConditionType.PRODUCT_CODE
        || condition.conditionType === 'product_code';
    const isGroupsPresent = condition.conditionType === CookingStepConditionType.ANY_INGREDIENT_GROUP_PRESENT
        || condition.conditionType === 'any_ingredient_group_present';
    const isKashaConsistency = condition.conditionType === CookingStepConditionType.KASHA_CONSISTENCY
        || condition.conditionType === 'kasha_consistency'
        || condition.conditionType === CookingStepConditionType.KASHA_CONSISTENCY_IN
        || condition.conditionType === 'kasha_consistency_in';
    const isKashaConsistencySingle = condition.conditionType === CookingStepConditionType.KASHA_CONSISTENCY
        || condition.conditionType === 'kasha_consistency';

    return (
        <div className="cooking-steps__condition-card">
            <label className="block__text recipe__form-label cooking-steps__condition-type">
                {t('app.page.cookingStep.conditionTypeLabel')}
                <Select
                    fullWidth
                    options={conditionTypeOptions}
                    value={condition.conditionType}
                    onChange={(nextValue) => {
                        const nextType = nextValue as CookingStepConditionType;
                        if (nextType === CookingStepConditionType.PRODUCT_FIELD) {
                            onChange({
                                conditionType: nextType,
                                ingredientGroup: condition.ingredientGroup || 'base',
                                fieldName: condition.fieldName || 'before_cooking_rinse_need',
                                operator: 'eq',
                                value: condition.value === 'false' ? 'false' : 'true',
                                productCode: '',
                            });
                            return;
                        }
                        if (nextType === CookingStepConditionType.PRODUCT_CODE) {
                            onChange({
                                conditionType: nextType,
                                ingredientGroup: '',
                                fieldName: '',
                                operator: '',
                                value: '',
                                productCode: condition.productCode || '',
                            });
                            return;
                        }
                        if (nextType === CookingStepConditionType.KASHA_CONSISTENCY) {
                            onChange({
                                conditionType: nextType,
                                ingredientGroup: '',
                                fieldName: '',
                                operator: 'eq',
                                value: condition.value || 'creamy',
                                productCode: '',
                            });
                            return;
                        }
                        if (nextType === CookingStepConditionType.KASHA_CONSISTENCY_IN) {
                            onChange({
                                conditionType: nextType,
                                ingredientGroup: '',
                                fieldName: '',
                                operator: 'in',
                                value: condition.value || '',
                                productCode: '',
                            });
                            return;
                        }
                        onChange({
                            conditionType: nextType,
                            ingredientGroup: '',
                            fieldName: '',
                            operator: '',
                            value: condition.value || '',
                            productCode: '',
                        });
                    }}
                />
            </label>

            {isProductField ? (
                <div className="cooking-steps__condition-fields">
                    <label className="block__text recipe__form-label cooking-steps__condition-field cooking-steps__condition-field_group">
                        {t('app.page.cookingStep.ingredientGroupLabel')}
                        <Select
                            fullWidth
                            options={ingredientGroupOptions}
                            value={condition.ingredientGroup || ''}
                            onChange={(nextValue) => onChange({ ingredientGroup: String(nextValue) })}
                        />
                        {errors.ingredientGroup ? (
                            <span className="cooking-steps__field-error">
                                {t('app.page.cookingStep.validation.required')}
                            </span>
                        ) : null}
                    </label>

                    <label className="block__text recipe__form-label cooking-steps__condition-field cooking-steps__condition-field_property">
                        {t('app.page.cookingStep.fieldName')}
                        <Select
                            fullWidth
                            options={fieldOptions}
                            value={selectedFieldValue}
                            onChange={(nextValue) => {
                                const nextField = String(nextValue);
                                if (nextField === PRODUCT_FIELD_OTHER) {
                                    onChange({
                                        fieldName: '',
                                        operator: 'eq',
                                        value: '',
                                    });
                                    return;
                                }
                                const def = getProductFieldDef(nextField);
                                onChange({
                                    fieldName: nextField,
                                    operator: 'eq',
                                    value: def?.valueType === 'boolean' ? 'true' : '',
                                });
                            }}
                        />
                        {errors.fieldName && selectedFieldValue !== PRODUCT_FIELD_OTHER ? (
                            <span className="cooking-steps__field-error">
                                {t('app.page.cookingStep.validation.required')}
                            </span>
                        ) : null}
                    </label>

                    {selectedFieldValue === PRODUCT_FIELD_OTHER ? (
                        <div className="cooking-steps__condition-field cooking-steps__condition-field_custom">
                            <Input
                                fullWidth
                                label={t('app.page.cookingStep.fieldNameCustom')}
                                value={condition.fieldName || ''}
                                onChange={(event) => onChange({ fieldName: event.target.value, operator: 'eq' })}
                            />
                            {errors.fieldName ? (
                                <span className="cooking-steps__field-error">
                                    {t('app.page.cookingStep.validation.required')}
                                </span>
                            ) : null}
                        </div>
                    ) : null}

                    {showBoolSelect ? (
                        <label className="block__text recipe__form-label cooking-steps__condition-field cooking-steps__condition-field_value">
                            {t('app.page.cookingStep.value')}
                            <Select
                                fullWidth
                                options={boolOptions}
                                value={condition.value || 'true'}
                                onChange={(nextValue) => onChange({
                                    operator: 'eq',
                                    value: String(nextValue),
                                })}
                            />
                            {errors.value ? (
                                <span className="cooking-steps__field-error">
                                    {t('app.page.cookingStep.validation.required')}
                                </span>
                            ) : null}
                        </label>
                    ) : (
                        <div className="cooking-steps__condition-field cooking-steps__condition-field_value">
                            <Input
                                fullWidth
                                label={t('app.page.cookingStep.value')}
                                value={condition.value || ''}
                                onChange={(event) => onChange({
                                    operator: 'eq',
                                    value: event.target.value,
                                })}
                            />
                            {errors.value ? (
                                <span className="cooking-steps__field-error">
                                    {t('app.page.cookingStep.validation.required')}
                                </span>
                            ) : null}
                        </div>
                    )}
                </div>
            ) : null}

            {isProductCode ? (
                <div className="cooking-steps__condition-fields">
                    <div className="cooking-steps__condition-field cooking-steps__condition-field_product">
                        <ProductCodeAutocomplete
                            value={condition.productCode || ''}
                            onChange={(productCode) => onChange({ productCode })}
                            hasError={Boolean(errors.productCode)}
                        />
                        {errors.productCode ? (
                            <span className="cooking-steps__field-error">
                                {t('app.page.cookingStep.validation.required')}
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {isGroupsPresent ? (
                <div className="cooking-steps__condition-fields">
                    <div className="cooking-steps__chips cooking-steps__condition-field cooking-steps__condition-field_chips">
                        <span className="cooking-steps__chips-label">
                            {t('app.page.cookingStep.groupsChipsLabel')}
                        </span>
                        <div className="cooking-steps__chips-list">
                            {INGREDIENT_GROUP_OPTIONS.map((group) => {
                                const active = selectedGroups.includes(group);
                                return (
                                    <Button
                                        key={group}
                                        type="button"
                                        size="small"
                                        variant={active ? 'default' : 'outline'}
                                        onClick={() => toggleGroup(group)}
                                    >
                                        {t(`app.page.cookingStep.ingredientGroup.${group}`)}
                                    </Button>
                                );
                            })}
                        </div>
                        {errors.value ? (
                            <span className="cooking-steps__field-error">
                                {t('app.page.cookingStep.validation.required')}
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {isKashaConsistency ? (
                <div className="cooking-steps__condition-fields">
                    <div className="cooking-steps__chips cooking-steps__condition-field cooking-steps__condition-field_chips">
                        <span className="cooking-steps__chips-label">
                            {t('app.page.cookingStep.kashaConsistencyChipsLabel')}
                        </span>
                        <div className="cooking-steps__chips-list">
                            {KASHA_CONSISTENCY_OPTIONS.map((code) => {
                                const active = isKashaConsistencySingle
                                    ? condition.value === code
                                    : selectedGroups.includes(code);
                                return (
                                    <Button
                                        key={code}
                                        type="button"
                                        size="small"
                                        variant={active ? 'default' : 'outline'}
                                        onClick={() => toggleConsistency(code)}
                                    >
                                        {t(`recipe.scheme.kashaConsistency.${code}`)}
                                    </Button>
                                );
                            })}
                        </div>
                        {errors.value ? (
                            <span className="cooking-steps__field-error">
                                {t('app.page.cookingStep.validation.required')}
                            </span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <div className="cooking-steps__condition-footer">
                <p className="cooking-steps__condition-preview">{preview}</p>
                <Button variant="outline" onClick={onRemove}>
                    {t('app.page.cookingStep.removeCondition')}
                </Button>
            </div>
        </div>
    );
};

export default ConditionRow;
